/// <reference types="vite-plugin-pwa/client" />

/**
 * AutoUpdater — the update orchestrator. Applies a detected update silently
 * when that is invisible/safe, and otherwise surfaces a quiet Reload bar so an
 * active session is never interrupted (fix-pwa-update-delivery, 2026-07-17;
 * supersedes the headless-only model of the 2026-06-12 amendment).
 *
 * Detection (two OR-ed paths):
 *   - useRegisterSW() (vite-plugin-pwa, `registerType: 'prompt'`): registers
 *     the Workbox SW and flips `needRefresh` when a new bundle is WAITING. The
 *     SW no longer skips-waiting on install (sw.ts), so this flag is now a
 *     reliable, recoverable signal on iOS standalone.
 *   - useExternalNeedRefresh() (version-probe.ts): polls /__app-version.json
 *     on visibilitychange→visible + every 15 min and flips on commit drift.
 *
 * Scheduling / auto-vs-prompt decision:
 *   (a) Arrival — detected within ARRIVAL_WINDOW_MS of page load or of the tab
 *       becoming visible: apply IMMEDIATELY (silent). The user just cold-
 *       launched or returned, so the reload is invisible. This preserves the
 *       "a refresh applies a queued update" property in a controlled way.
 *   (b) Hidden — the tab is hidden: apply IMMEDIATELY (silent, off-screen).
 *   (c) Visible mid-session — the user may be reading or editing: DO NOT force
 *       a reload. Show the quiet UpdateBar and let the user choose. If the tab
 *       then goes hidden, apply silently (a safe, invisible moment). Recipe
 *       bodies are CRDT-persisted, but a forced reload mid-keystroke is still
 *       jarring — so mid-session activation is always the user's choice.
 *
 * Apply = applyUpdate() (apply-update.ts) — the multi-layer flush. One
 * console.info line marks the apply for diagnosability.
 *
 * Passive tabs: a controllerchange listener reloads this tab when ANOTHER
 * tab activated a new SW (otherwise this tab keeps running an old bundle
 * against the new precache and its stale lazy-chunks 404). Invariant: only a
 * controller REPLACEMENT reloads, never the first claim — on a brand-new
 * visit the first SW install activates and calls clients.claim(), flipping
 * `controller` from null to set, and the page is already running the
 * freshly-installed bundle. The isApplyingUpdate() flag prevents a
 * double-reload in the initiating tab — applyUpdate() already ends in its
 * own navigation.
 */

import { useCallback, useEffect, useRef, useState, type ReactElement } from "react";
import { useRegisterSW } from "virtual:pwa-register/react";
import { useExternalNeedRefresh } from "./version-probe";
import { applyUpdate, isApplyingUpdate } from "./apply-update";
import { initUpdateTiming, isArrivalMoment } from "./update-timing";
import { UpdateBar } from "./UpdateBar";

/** Detections within this window of load / return-to-tab apply immediately. */
export const ARRIVAL_WINDOW_MS = 10_000;

export function AutoUpdater(): ReactElement | null {
  const {
    needRefresh: [needRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    // Rule 8: surface SW registration errors via console.error so an upstream
    // monitor (Track 4.R window.onerror) can see them. Don't silently swallow.
    onRegisterError(error: unknown) {
      console.error("[proof SW] Registration failed:", error);
    },
  });

  const externalNeedRefresh = useExternalNeedRefresh();
  const updateAvailable = needRefresh || externalNeedRefresh;

  // When true, render the quiet Reload bar (visible mid-session path).
  const [showBar, setShowBar] = useState(false);

  // Latest values for the deferred (visibilitychange / user-tap) apply paths —
  // the scheduling effect deliberately keys on `updateAvailable` only.
  const needRefreshRef = useRef(needRefresh);
  needRefreshRef.current = needRefresh;
  const updateServiceWorkerRef = useRef(updateServiceWorker);
  updateServiceWorkerRef.current = updateServiceWorker;

  // Single apply path shared by the silent scheduler and the Reload button.
  const apply = useCallback((): void => {
    if (isApplyingUpdate()) return; // an apply is already in flight
    console.info("[proof] New version detected. Applying update.");
    void applyUpdate({
      updateServiceWorker: updateServiceWorkerRef.current,
      needRefresh: needRefreshRef.current,
    });
  }, []);

  // Start the interaction/visibility tracker as early as possible (idempotent).
  useEffect(() => {
    initUpdateTiming();
  }, []);

  // Passive-tab reload: another tab (or window) activated a new SW. Without
  // this, the current tab keeps running the old bundle against the new
  // precache and its stale lazy-chunk URLs 404. Invariant: only a controller
  // REPLACEMENT reloads, never the first claim. Two guards enforce it:
  //   - First-claim guard: on a brand-new visit the FIRST SW install
  //     activates and calls clients.claim(), flipping `controller` from null
  //     to set. That is a new install, not an update — the page is already
  //     running the freshly-installed bundle, and reloading would yank every
  //     first-time visitor mid-flow (e.g. mid-signup).
  //   - isApplyingUpdate(): when THIS tab initiated the apply, applyUpdate()
  //     navigates via location.replace — reloading here too would
  //     double-navigate.
  useEffect(() => {
    const sw = typeof navigator !== "undefined" ? navigator.serviceWorker : undefined;
    if (!sw || typeof sw.addEventListener !== "function") return undefined;

    let hadController = sw.controller !== null;

    const onControllerChange = (): void => {
      if (!hadController) {
        // First claim — record that the page is now controlled, don't reload.
        hadController = true;
        return;
      }
      if (isApplyingUpdate()) return;
      window.location.reload();
    };

    sw.addEventListener("controllerchange", onControllerChange);
    return () => {
      sw.removeEventListener("controllerchange", onControllerChange);
    };
  }, []);

  // Scheduling: runs when either detection flag flips true.
  useEffect(() => {
    if (!updateAvailable) {
      setShowBar(false);
      return undefined;
    }

    // (a) Arrival moment — detected on first load or right after the user
    // returned to the tab. Apply immediately; the reload is invisible.
    if (isArrivalMoment(ARRIVAL_WINDOW_MS)) {
      apply();
      return undefined;
    }

    // (b) Tab hidden — apply immediately; the reload happens off-screen.
    if (typeof document !== "undefined" && document.hidden) {
      apply();
      return undefined;
    }

    // (c) Visible mid-session — do NOT force a reload. Surface the quiet
    // Reload bar and let the user choose when. If the tab goes hidden before
    // they act, apply silently (a safe, invisible moment).
    setShowBar(true);

    const onVisibilityChange = (): void => {
      if (document.hidden) {
        setShowBar(false);
        apply();
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [updateAvailable, apply]);

  if (showBar) {
    return <UpdateBar onReload={apply} />;
  }
  return null;
}
